import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';
import { useParams } from 'react-router-dom';

import { ArtifactBlock } from './artifact-block';
import { Reference } from './reference';
import {
  ArtifactBlock as ArtifactBlockType,
  ContentBlock,
  KimMessageType,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

const SHOW_DEBUG_INFO = false;

export const REF_PLACEHOLDER_TAG = 'kimref';
const ARTIFACT_PLACEHOLDER_TAG = 'kimartifact';

export function formatContent(blocks: ContentBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'text') return block.value;
      if (block.type === 'artifact')
        return `<${ARTIFACT_PLACEHOLDER_TAG} data-id="${block.id}-${block.versionNumber}"></${ARTIFACT_PLACEHOLDER_TAG}>`;
      return `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
    })
    .join('');
}

export function KimMessage({
  type,
  content,
  isComplete,
  referenceIndexMap,
  debugLabel,
  onReferenceClick,
  artifactsById,
  latestArtifactId,
}: {
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  debugLabel: string;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  artifactsById?: Map<string, ArtifactBlockType>;
  latestArtifactId?: string | null;
}) {
  const { matterId, subsetId } = useParams();
  const isUser = type === KimMessageType.User;
  const [copied, setCopied] = useState(false);

  const firstReference = useMemo(() => {
    const ref = content.find(
      (block) =>
        block.type === 'reference' &&
        (block.refType === ReferenceType.Fact ||
          block.refType === ReferenceType.Chunk ||
          block.refType === ReferenceType.Document),
    );
    if (ref && ref.type === 'reference') return { id: ref.id, refType: ref.refType };
    return null;
  }, [content]);

  const rendered = useMemo(() => {
    if (isUser) {
      const raw = content
        .filter((block) => block.type === 'text')
        .map((block) => block.value)
        .join('');

      // Render doc mentions as links to the doc page
      const parts: (string | JSX.Element)[] = [];
      const docMentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = docMentionRegex.exec(raw)) !== null) {
        if (match.index > lastIndex) {
          parts.push(raw.slice(lastIndex, match.index));
        }
        const [, fileName, docId] = match;
        parts.push(
          <a
            key={match.index}
            href={`/matters/${matterId}/subsets/${subsetId}/docs/${docId}`}
            target="_blank"
            className="group"
          >
            {fileName}
            <Icon className="lucide--external-link mr-0.5 -mb-px ml-1.25 inline-block text-xs" />
          </a>,
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < raw.length) {
        parts.push(raw.slice(lastIndex));
      }

      return parts.some((p) => typeof p !== 'string') ? parts : raw;
    }

    // Build a single markdown string with placeholders for references
    const combined = formatContent(content);

    const rawHtml = marked.parse(combined, { async: false }) as string;
    // Remove spaces between refs
    const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');

    return parse(html, {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
          const id = domNode.attribs['data-id'];
          const refType = domNode.attribs['data-ref-type'] as ReferenceType;
          return (
            <Reference
              index={referenceIndexMap.get(id)?.index ?? 0}
              id={id}
              referenceType={refType}
              onClick={
                refType === ReferenceType.Fact || refType === ReferenceType.Chunk || refType === ReferenceType.Document
                  ? onReferenceClick
                  : undefined
              }
            />
          );
        }
        if (domNode instanceof Element && domNode.name === ARTIFACT_PLACEHOLDER_TAG) {
          const id = domNode.attribs['data-id'];
          const artifact = id ? artifactsById?.get(id) : undefined;
          if (!artifact) return null;
          return (
            <ArtifactBlock
              id={id}
              content={artifact.content}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
              collapsed={!!id && id !== latestArtifactId}
              version={artifact?.versionNumber}
            />
          );
        }
      },
    });
  }, [content, isUser, referenceIndexMap, onReferenceClick, artifactsById, latestArtifactId]);

  const copyToClipboard = () => {
    const markdown = content
      .filter((block) => block.type === 'text')
      .map((block) => block.value)
      .join('');
    navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={cn('flex', isUser ? 'justify-end' : '')} {...(isUser ? { 'data-user-message': '' } : {})}>
      <div
        className={cn(
          // the [font-feature-settings:\'calt\'_0] disables converting (1) into a weird unicode char
          "rounded-xl py-2 [font-feature-settings:'calt'_0]",
          isUser ? 'my-8 max-w-4/5 rounded-br-none border border-gray-300 bg-gray-100 px-3.5 pt-1.5' : 'w-full',
        )}
      >
        {SHOW_DEBUG_INFO && (
          <div className="mb-1 font-mono text-xs text-gray-400">
            {debugLabel} | type: {type}
          </div>
        )}

        <div className="flex items-start gap-3.25">
          {type === KimMessageType.AiReasoning && (
            <div className="mt-2.25 size-1.25 shrink-0 rounded-full bg-gray-300" />
          )}
          <div
            className={cn(
              'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold max-w-none',
              type === KimMessageType.AiReasoning && 'prose-p:text-gray-600 italic',
            )}
          >
            {rendered}
          </div>
        </div>

        {type === KimMessageType.Ai && isComplete && (
          <motion.div
            className="mt-6 flex w-full justify-between gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, delay: 0.2 }}
          >
            <div className="flex gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" onClick={copyToClipboard} className="p-1 px-1.5">
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.span
                          key="check"
                          initial={{ opacity: 0, filter: 'blur(2px)' }}
                          animate={{ opacity: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, filter: 'blur(2px)' }}
                          transition={{ duration: 0.1 }}
                        >
                          <Icon className="lucide--check" />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="copy"
                          initial={{ opacity: 0, filter: 'blur(2px)' }}
                          animate={{ opacity: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, filter: 'blur(2px)' }}
                          transition={{ duration: 0.1 }}
                        >
                          <Icon className="lucide--copy" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Button>
                </TooltipTrigger>

                <TooltipContent>Copy answer to clipboard</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" className="p-1 px-1.5">
                    <Icon className="lucide--arrow-down-to-line" />
                  </Button>
                </TooltipTrigger>

                <TooltipContent>Download as Word document</TooltipContent>
              </Tooltip>
            </div>

            <div>
              {firstReference && onReferenceClick && (
                <Button
                  variant={'ghost'}
                  size={'sm'}
                  className="font-inter group text-gray-600"
                  onClick={() => onReferenceClick(firstReference.id, firstReference.refType)}
                >
                  View references in answer
                  <Icon className="lucide--book-open" />
                  <Icon className="lucide--chevron-right -ml-2 opacity-0 duration-150 group-hover:ml-1 group-hover:opacity-100" />
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
